// src/components/settings/NumberingSettings.js
//
// Org-level document numbering. Seam 1B: the invoice starting-number control.
//
// Unlike DepositSettings (a direct Firestore client write), the Save handler
// here calls the `setInvoiceStartingNumber` Cloud Function. Choosing where
// numbering starts is duplicate-sensitive: the backend computes a FLOOR from the
// numbers an org has actually issued (counter value AND any migrated pre-set
// invoice numbers the counter never advanced past) and rejects anything at or
// below it. That scan and the counter write belong server-side, so the client
// never computes the floor — it only shows what the callable returns.
//
// This page never touches existing invoices: it only governs the number the
// NEXT invoice will receive. Anything already issued keeps its number.
import React, { useState, useEffect, useRef } from 'react';
import { db, functions } from '../../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Hash, AlertTriangle, CheckCircle, CalendarDays } from 'lucide-react';

export default function NumberingSettings({ currentUserProfile }) {
    const orgId = currentUserProfile?.organizationId;

    // The counter's current value — the number the next invoice will receive.
    const [nextInvoiceNumber, setNextInvoiceNumber] = useState('0001');
    // The operator's requested starting number (kept as a string so the field
    // can be empty while editing).
    const [startInput, setStartInput] = useState('');
    const [isSaving,  setIsSaving]  = useState(false);
    const [saved,     setSaved]     = useState(null);   // { saved: '0043' } on success
    const [error,     setError]     = useState('');
    const [loading,   setLoading]   = useState(true);

    // Seed the input from the live counter ONCE, so subsequent snapshots (e.g.
    // an invoice advancing the counter) don't yank the field out from under the
    // operator mid-edit.
    const seededRef = useRef(false);

    // Seam 2C: parallel state for the estimate starting number, mirroring the
    // invoice controls above. Kept fully separate (not shared) so a save on one
    // side never touches the other's field, error, or "Saved" message, and the
    // invoice control keeps behaving exactly as Seam 1B shipped.
    const [nextQuoteNumber, setNextQuoteNumber] = useState('0001');
    const [estStartInput, setEstStartInput] = useState('');
    const [estIsSaving, setEstIsSaving] = useState(false);
    const [estSaved,     setEstSaved]     = useState(null);
    const [estError,     setEstError]     = useState('');
    const estSeededRef = useRef(false);

    // numbering-jobstart-5d2b8e61 — job twin of the estimate state above (Parts,
    // Services & Inventory, Phase 0 Seam 1). Separate again, so a job save never
    // touches the invoice or estimate field, error or "Saved" message. The server
    // (setJobStartingNumber) is the authority on the floor, same as the other two.
    const [nextJobNumber, setNextJobNumber] = useState('0001');
    const [jobStartInput, setJobStartInput] = useState('');
    const [jobIsSaving, setJobIsSaving] = useState(false);
    const [jobSaved,     setJobSaved]     = useState(null);
    const [jobError,     setJobError]     = useState('');
    const jobSeededRef = useRef(false);

    useEffect(() => {
        if (!orgId) return;
        return onSnapshot(doc(db, 'organizations', orgId), (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                const next = data.nextInvoiceNumber || '0001';
                setNextInvoiceNumber(next);
                if (!seededRef.current) {
                    const n = parseInt(next, 10);
                    setStartInput(Number.isFinite(n) ? String(n) : '');
                    seededRef.current = true;
                }
                // Seam 2C: same one-time seed for the estimate counter. Orgs that
                // predate the estimate counter have no nextQuoteNumber yet, so it
                // falls back to '0001' exactly like the trigger does.
                const nextQ = data.nextQuoteNumber || '0001';
                setNextQuoteNumber(nextQ);
                if (!estSeededRef.current) {
                    const nq = parseInt(nextQ, 10);
                    setEstStartInput(Number.isFinite(nq) ? String(nq) : '');
                    estSeededRef.current = true;
                }
                // numbering-jobstart-5d2b8e61 — same one-time seed for the job
                // counter; '0001' fallback matches the numbering trigger.
                const nextJ = data.nextJobNumber || '0001';
                setNextJobNumber(nextJ);
                if (!jobSeededRef.current) {
                    const nj = parseInt(nextJ, 10);
                    setJobStartInput(Number.isFinite(nj) ? String(nj) : '');
                    jobSeededRef.current = true;
                }
            }
            setLoading(false);
        });
    }, [orgId]);

    const handleSave = async () => {
        setError('');
        setSaved(null);

        // Client-side guard so the field can't submit something the backend would
        // reject as malformed. The backend remains the authority on the floor;
        // this only catches empty / non-positive-integer input up front.
        const requested = Math.trunc(Number(startInput));
        if (startInput === '' || !Number.isFinite(requested) || requested < 1) {
            setError('Enter a whole number of 1 or more.');
            return;
        }

        setIsSaving(true);
        try {
            const setStart = httpsCallable(functions, 'setInvoiceStartingNumber');
            const res = await setStart({ startingNumber: requested });
            // Callable returns { ok, floor, saved } — `saved` is the padded value
            // now stored on the counter (e.g. "0043").
            setSaved({ saved: res.data?.saved || String(requested).padStart(4, '0') });
            setTimeout(() => setSaved(null), 6000);
        } catch (err) {
            // A below-floor or malformed rejection arrives as an HttpsError whose
            // message already names the number the operator must exceed — show it
            // verbatim rather than inventing our own wording. Nothing was written.
            console.error('Error setting invoice starting number:', err);
            setError(err?.message || 'Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // Seam 2C: estimate twin of handleSave. Identical flow — client-side guard,
    // then the callable is the authority on the floor — but calls
    // setEstimateStartingNumber and writes only the estimate-side state.
    const handleSaveEstimate = async () => {
        setEstError('');
        setEstSaved(null);

        const requested = Math.trunc(Number(estStartInput));
        if (estStartInput === '' || !Number.isFinite(requested) || requested < 1) {
            setEstError('Enter a whole number of 1 or more.');
            return;
        }

        setEstIsSaving(true);
        try {
            const setStart = httpsCallable(functions, 'setEstimateStartingNumber');
            const res = await setStart({ startingNumber: requested });
            setEstSaved({ saved: res.data?.saved || String(requested).padStart(4, '0') });
            setTimeout(() => setEstSaved(null), 6000);
        } catch (err) {
            console.error('Error setting estimate starting number:', err);
            setEstError(err?.message || 'Failed to save. Please try again.');
        } finally {
            setEstIsSaving(false);
        }
    };

    // numbering-jobstart-5d2b8e61 — job twin of handleSaveEstimate: same guard,
    // then setJobStartingNumber decides; its error is shown word for word.
    const handleSaveJob = async () => {
        setJobError('');
        setJobSaved(null);

        const requested = Math.trunc(Number(jobStartInput));
        if (jobStartInput === '' || !Number.isFinite(requested) || requested < 1) {
            setJobError('Enter a whole number of 1 or more.');
            return;
        }

        setJobIsSaving(true);
        try {
            const setStart = httpsCallable(functions, 'setJobStartingNumber');
            const res = await setStart({ startingNumber: requested });
            setJobSaved({ saved: res.data?.saved || String(requested).padStart(4, '0') });
            setTimeout(() => setJobSaved(null), 6000);
        } catch (err) {
            console.error('Error setting job starting number:', err);
            setJobError(err?.message || 'Failed to save. Please try again.');
        } finally {
            setJobIsSaving(false);
        }
    };

    if (loading) return <div className="p-6 text-sm text-gray-400">Loading…</div>;

    return (
        <div className="p-6 space-y-6 max-w-2xl">
            {/* SEAM 1B VERIFIER: numbering-invoicestart-4f8b1a27 */}
            <div>
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <Hash size={18} className="text-gray-500" /> Numbering
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    Choose where your invoice, estimate and job numbering starts. This only changes
                    what you create from now on — anything you've already created keeps its number.
                </p>
            </div>

            {/* numbering-jobstart-5d2b8e61 — "Invoices" heading to match "Estimates" and "Jobs" */}
            <h3 className="text-base font-bold text-gray-800">Invoices</h3>

            {/* Current next number */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Next invoice number
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{nextInvoiceNumber}</p>
                <p className="text-xs text-gray-400 mt-1">
                    The next invoice you create will be numbered {nextInvoiceNumber}.
                </p>
            </div>

            {/* Starting-number control */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                        Start invoice numbers at
                    </label>
                    <input
                        type="number"
                        min="1"
                        step="1"
                        value={startInput}
                        onChange={(e) => setStartInput(
                            e.target.value === ''
                                ? ''
                                : String(Math.max(1, Math.trunc(Number(e.target.value))))
                        )}
                        placeholder="1"
                        className="w-40 p-2 border border-gray-300 rounded-lg text-sm text-right font-semibold focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                </div>
                <p className="text-xs text-gray-400">
                    If you moved over from another system, set this to match where your
                    invoice numbers left off. You can't start below a number you've already
                    used — we'll tell you the lowest number you can pick.
                </p>
            </div>

            {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {error}
                </div>
            )}

            <div className="flex items-center gap-3">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                    {isSaving ? 'Saving…' : 'Save'}
                </button>
                {saved && (
                    <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                        <CheckCircle size={14} />
                        Saved. New invoices will start at {saved.saved}. Invoices you've already created keep their numbers.
                    </span>
                )}
            </div>

            {/* SEAM 2C VERIFIER: numbering-estimatestart-2e9a7c14 — estimate starting number */}
            <div className="pt-4 border-t border-gray-200">
                <h3 className="text-base font-bold text-gray-800">Estimates</h3>
                <p className="text-sm text-gray-500 mt-1">
                    Choose where your estimate numbering starts. New estimates get your
                    prefix followed by this number (for example E-0001 or Q-0001); this only
                    changes estimates you create from now on.
                </p>
            </div>

            {/* Current next estimate number */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Next estimate number
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{nextQuoteNumber}</p>
                <p className="text-xs text-gray-400 mt-1">
                    The next estimate you create will use your E-/Q- prefix followed by {nextQuoteNumber}.
                </p>
            </div>

            {/* Estimate starting-number control */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                        Start estimate numbers at
                    </label>
                    <input
                        type="number"
                        min="1"
                        step="1"
                        value={estStartInput}
                        onChange={(e) => setEstStartInput(
                            e.target.value === ''
                                ? ''
                                : String(Math.max(1, Math.trunc(Number(e.target.value))))
                        )}
                        placeholder="1"
                        className="w-40 p-2 border border-gray-300 rounded-lg text-sm text-right font-semibold focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                </div>
                <p className="text-xs text-gray-400">
                    If you moved over from another system, set this to match where your
                    estimate numbers left off. You can't start below a number you've already
                    used — we'll tell you the lowest number you can pick.
                </p>
            </div>

            {estError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {estError}
                </div>
            )}

            <div className="flex items-center gap-3">
                <button
                    onClick={handleSaveEstimate}
                    disabled={estIsSaving}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                    {estIsSaving ? 'Saving…' : 'Save'}
                </button>
                {estSaved && (
                    <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                        <CheckCircle size={14} />
                        Saved. New estimates will start at {estSaved.saved}. Estimates you've already created keep their numbers.
                    </span>
                )}
            </div>

            {/* numbering-jobstart-5d2b8e61 — job starting number (setJobStartingNumber) */}
            <div className="pt-4 border-t border-gray-200">
                <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                    Jobs
                    <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-[#5F4EC9] text-[10px] font-bold uppercase tracking-wide">
                        New
                    </span>
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                    Choose where your job numbering starts. Spatchy gives every new job the next
                    number — from the web, a phone or a recurring schedule — so no two jobs ever share one.
                </p>
            </div>

            {/* Current next job number */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Next job number
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{nextJobNumber}</p>
                <p className="text-xs text-gray-400 mt-1">
                    The next job you create will be numbered {nextJobNumber}.
                </p>
            </div>

            {/* Job starting-number control */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                        Start job numbers at
                    </label>
                    <input
                        type="number"
                        min="1"
                        step="1"
                        value={jobStartInput}
                        onChange={(e) => setJobStartInput(
                            e.target.value === ''
                                ? ''
                                : String(Math.max(1, Math.trunc(Number(e.target.value))))
                        )}
                        placeholder="1"
                        className="w-40 p-2 border border-gray-300 rounded-lg text-sm text-right font-semibold focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                </div>
                <p className="text-xs text-gray-400">
                    If you moved over from another system, set this to match where your
                    job numbers left off. You can't start below a number you've already
                    used — we'll tell you the lowest number you can pick.
                </p>
            </div>

            {/* Year-numbering hint */}
            <div className="flex items-start gap-2 p-3 bg-purple-50 border border-purple-100 rounded-xl text-xs text-gray-600">
                <CalendarDays size={14} className="shrink-0 mt-0.5 text-[#5F4EC9]" />
                <span>
                    Number your jobs by year? Spatchy never restarts numbers on its own. On January 1,
                    change this yourself — for example, from 26000 to 27000.
                </span>
            </div>

            {jobError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {jobError}
                </div>
            )}

            <div className="flex items-center gap-3">
                <button
                    onClick={handleSaveJob}
                    disabled={jobIsSaving}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                    {jobIsSaving ? 'Saving…' : 'Save'}
                </button>
                {jobSaved && (
                    <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                        <CheckCircle size={14} />
                        Saved. New jobs will start at {jobSaved.saved}. Jobs you've already created keep their numbers.
                    </span>
                )}
            </div>
        </div>
    );
}
